import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import {
  RefreshCw, Database, Globe2, Radio, ShieldCheck, Search, Building2,
  CheckCircle2, AlertCircle, XCircle, ExternalLink,
} from "lucide-react";
import api from "../../../api";
import AiSeoReviewQueue from "./AiSeoReviewQueue";

// Native CRM-side view of the AI-SEO / Property Brain connection. It signs on to
// AI-SEO with a short-lived token the CRM mints (the shared account), then calls
// AI-SEO's own /v1/crm-bridge read API directly to render the cockpit — no
// iframe. Actioning drafts (edit/approve/publish) is left to the full app, so
// there's an "Open the full app" button rather than a native editor.
const AISEO_API = (process.env.REACT_APP_AISEO_API_URL || "https://collings-ai.onrender.com/v1").replace(/\/+$/, "");

const EVENT_LABEL = {
  property_sold: "Sold",
  listing_published: "New listing",
  agent_requested: "Agent request",
};

function timeAgo(v) {
  if (!v) return "—";
  const s = Math.max(0, Math.floor((Date.now() - new Date(v).getTime()) / 1000));
  if (Number.isNaN(s)) return "—";
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

function Dot({ ok, warn }) {
  const I = ok ? CheckCircle2 : warn ? AlertCircle : XCircle;
  return <I className={`w-4 h-4 ${ok ? "text-emerald-500" : warn ? "text-amber-500" : "text-red-500"}`} />;
}

function Tile({ icon: Icon, name, ok, warn, status, lines }) {
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4">
      <div className="flex items-center gap-2.5">
        <span className="grid place-items-center w-7 h-7 rounded-lg bg-gray-100 text-teal-600"><Icon className="w-4 h-4" /></span>
        <span className="font-semibold text-sm text-gray-900">{name}</span>
        <span className="ml-auto inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide"><Dot ok={ok} warn={warn} /> {status}</span>
      </div>
      <div className="mt-3 space-y-0.5 text-xs text-gray-500">
        {lines.filter(Boolean).map((l, i) => <div key={i}>{l}</div>)}
      </div>
    </div>
  );
}

const AiSeoNativeView = ({ onOpenFullApp }) => {
  const [token, setToken] = useState(null);
  const [siteId, setSiteId] = useState(null);
  const [health, setHealth] = useState(null);
  const [sales, setSales] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [pSuburb, setPSuburb] = useState("");
  const [pAgent, setPAgent] = useState("");
  const [prov, setProv] = useState(null);
  const [provLoading, setProvLoading] = useState(false);

  // Sign on to AI-SEO as the shared account (via the CRM-minted token) and
  // resolve the site to scope the read calls to.
  const bootstrap = useCallback(async () => {
    // 1) mint SSO token on the CRM
    const t = await api.get("/api/aiseo/sso-token/");
    // 2) exchange it for an AI-SEO session
    const sso = await axios.post(`${AISEO_API}/auth/sso`, { token: t.data.token });
    const accessToken = sso.data?.accessToken;
    if (!accessToken) throw new Error("SSO did not return a session");
    // 3) resolve the site
    const sites = await axios.get(`${AISEO_API}/sites`, { headers: { Authorization: `Bearer ${accessToken}` } });
    const list = sites.data?.sites || sites.data || [];
    const site = list[0];
    const sid = site && (site._id || site.id);
    if (!sid) throw new Error("No AI-SEO site available");
    return { accessToken, sid };
  }, []);

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const { accessToken, sid } = await bootstrap();
      setToken(accessToken); setSiteId(sid);
      const h = { headers: { Authorization: `Bearer ${accessToken}` } };
      const [hz, sm, ev] = await Promise.all([
        axios.get(`${AISEO_API}/crm-bridge/${sid}/health`, h),
        axios.get(`${AISEO_API}/crm-bridge/${sid}/sales-summary`, h),
        axios.get(`${AISEO_API}/crm-bridge/${sid}/events?limit=15`, h),
      ]);
      setHealth(hz.data); setSales(sm.data); setEvents(ev.data?.events || []);
    } catch (e) {
      setError(
        e?.response?.status === 503
          ? "AI-SEO single sign-on isn't switched on yet. Use the Full app view and log in there, or ask an admin to enable SSO."
          : (e?.response?.data?.error || e.message || "Couldn't reach AI-SEO."),
      );
    } finally {
      setLoading(false);
    }
  }, [bootstrap]);

  useEffect(() => { load(); }, [load]);

  const runProvenance = useCallback(async () => {
    if (!token || !siteId) return;
    setProvLoading(true);
    try {
      const q = new URLSearchParams();
      if (pSuburb.trim()) q.set("suburb", pSuburb.trim());
      if (pAgent.trim()) q.set("agent", pAgent.trim());
      const r = await axios.get(`${AISEO_API}/crm-bridge/${siteId}/provenance?${q}`, { headers: { Authorization: `Bearer ${token}` } });
      setProv(r.data);
    } catch { /* leave prior result */ } finally { setProvLoading(false); }
  }, [token, siteId, pSuburb, pAgent]);

  const flags = health?.flags || {};
  const brainOk = !!health?.brain?.ok;
  const crmOk = !!health?.crmApi?.ok;
  const eventsOn = !!flags.propertyEventsEnabled && !!flags.crmEventsTokenSet;
  const gateMode = flags.publishPermissionMode || "shadow";
  const gateOn = !!flags.publishPermissionEnabled;

  if (loading) {
    return <div className="flex items-center gap-2 text-gray-500 text-sm p-6"><RefreshCw className="w-4 h-4 animate-spin" /> Connecting to AI-SEO…</div>;
  }
  if (error) {
    return (
      <div className="p-6">
        <div className="rounded-xl border border-amber-200 bg-amber-50 text-amber-800 text-sm p-4 max-w-2xl">{error}</div>
        <button onClick={onOpenFullApp} className="mt-3 inline-flex items-center gap-2 text-sm font-medium px-3 py-2 rounded-lg bg-gray-900 text-white hover:bg-black">
          <ExternalLink className="w-4 h-4" /> Open the full app
        </button>
      </div>
    );
  }

  return (
    <div className="pr-1">
      <div className="flex items-center justify-between mb-3">
        <p className="text-sm text-gray-500">Live connection status and recent activity. Read-only — open the full app to review and publish drafts.</p>
        <div className="flex items-center gap-2">
          <button onClick={load} className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50"><RefreshCw className="w-4 h-4" /> Refresh</button>
          <button onClick={onOpenFullApp} className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-lg bg-teal-600 text-white hover:bg-teal-700"><ExternalLink className="w-4 h-4" /> Open full app</button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
        <Tile icon={Database} name="Property Brain" ok={brainOk} warn={!brainOk && health?.brain?.configured}
          status={brainOk ? "Live" : health?.brain?.configured ? "Degraded" : "Off"}
          lines={["Read-only pool", brainOk ? `latency ${health.brain.latencyMs ?? "—"} ms` : (health?.brain?.reason || "unreachable")]} />
        <Tile icon={Globe2} name="CRM Service API" ok={crmOk} warn={!crmOk && health?.crmApi?.configured}
          status={crmOk ? "Live" : health?.crmApi?.configured ? "Down" : "Off"}
          lines={["permissions + evidence", crmOk ? `ping ${health.crmApi.latencyMs ?? "—"} ms` : (health?.crmApi?.reason || "not reachable")]} />
        <Tile icon={Radio} name="Event webhook" ok={eventsOn} warn={!eventsOn && flags.crmEventsTokenSet}
          status={eventsOn ? "Enabled" : "Off"}
          lines={["crm/property-event", `token ${flags.crmEventsTokenSet ? "set" : "unset"} · events ${flags.propertyEventsEnabled ? "on" : "off"}`]} />
        <Tile icon={ShieldCheck} name="Permission gate" ok={gateOn && gateMode === "enforce"} warn={gateOn && gateMode === "shadow"}
          status={!gateOn ? "Off" : gateMode === "enforce" ? "Enforce" : "Shadow"}
          lines={[gateMode === "shadow" ? "logging only — blocks nothing yet" : "actively holding non-publishable", gateOn ? `mode: ${gateMode}` : "disabled"]} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1.5fr_1fr] gap-4 items-start">
        <div className="rounded-xl border border-gray-200 bg-white p-4">
          <h2 className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-3">Recent events → drafts</h2>
          {events.length === 0 ? (
            <div className="text-sm text-gray-400 py-8 text-center">No CRM events yet.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="text-[11px] uppercase tracking-wide text-gray-400 border-b border-gray-100">
                  <th className="text-left font-medium py-2 pr-3">When</th><th className="text-left font-medium py-2 pr-3">Event</th>
                  <th className="text-left font-medium py-2 pr-3">Property</th><th className="text-left font-medium py-2">Agent</th></tr></thead>
                <tbody>
                  {events.map((ev) => (
                    <tr key={ev.id} className="border-b border-gray-50 last:border-0">
                      <td className="py-2.5 pr-3 whitespace-nowrap text-gray-400">{timeAgo(ev.at)}</td>
                      <td className="py-2.5 pr-3"><span className="text-[11px] font-semibold px-2 py-0.5 rounded-md bg-teal-50 text-teal-700 border border-teal-200">{EVENT_LABEL[ev.event] || ev.event}</span></td>
                      <td className="py-2.5 pr-3"><span className="font-medium text-gray-800">{ev.suburb || "—"}</span>{ev.propertyPk != null && <span className="text-gray-400"> · #{ev.propertyPk}</span>}</td>
                      <td className="py-2.5 text-gray-500">{ev.agent || ev.requestedBy || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="rounded-xl border border-gray-200 bg-white p-4">
          <h2 className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-3">First-party sales</h2>
          {sales && sales.ok === false ? (
            <div className="text-sm text-gray-400 py-6 text-center">Brain not configured.</div>
          ) : (
            <>
              <div className="flex items-baseline gap-2">
                <span className="text-4xl font-bold text-gray-900">{sales?.total ?? "—"}</span>
                <span className="text-xs text-gray-500">sold listings in the brain</span>
              </div>
              <div className="mt-2 flex items-center gap-2 text-[11px] text-gray-500">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                {sales?.lastSync ? `synced ${timeAgo(sales.lastSync)}` : "awaiting first sync"} · {sales?.suburbs ?? 0} suburbs
              </div>
              <div className="mt-4 space-y-2">
                {(sales?.bySuburb || []).map((r) => {
                  const max = Math.max(1, ...(sales.bySuburb || []).map((x) => x.n));
                  return (
                    <div key={r.suburb} className="grid grid-cols-[80px_1fr_28px] items-center gap-2 text-[11px]">
                      <span className="text-gray-500 truncate">{r.suburb}</span>
                      <span className="h-1.5 rounded-full bg-gray-100 overflow-hidden"><span className="block h-full rounded-full bg-teal-500" style={{ width: `${Math.round((r.n / max) * 100)}%` }} /></span>
                      <span className="text-right text-gray-400">{r.n}</span>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </div>

      {token && siteId && (
        <div className="mt-4">
          <AiSeoReviewQueue token={token} siteId={siteId} />
        </div>
      )}

      <div className="rounded-xl border border-gray-200 bg-white p-4 mt-4">
        <h2 className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-3">Provenance lookup</h2>
        <div className="flex flex-wrap gap-2 items-center">
          <div className="relative flex-1 min-w-[160px]">
            <Building2 className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input value={pSuburb} onChange={(e) => setPSuburb(e.target.value)} onKeyDown={(e) => e.key === "Enter" && runProvenance()} placeholder="Suburb (e.g. Preston)" className="w-full pl-9 pr-3 py-2 text-sm rounded-lg border border-gray-200 text-gray-800" />
          </div>
          <input value={pAgent} onChange={(e) => setPAgent(e.target.value)} onKeyDown={(e) => e.key === "Enter" && runProvenance()} placeholder="Agent (optional)" className="flex-1 min-w-[140px] px-3 py-2 text-sm rounded-lg border border-gray-200 text-gray-800" />
          <button onClick={runProvenance} disabled={provLoading} className="inline-flex items-center gap-1.5 text-sm font-medium px-4 py-2 rounded-lg bg-teal-600 text-white hover:bg-teal-700 disabled:opacity-50"><Search className="w-4 h-4" /> Trace</button>
        </div>
        {prov && prov.ok && (
          <div className="mt-4 rounded-lg border border-gray-100 bg-gray-50 p-4">
            <div className="text-sm font-semibold text-gray-800">
              <span className="text-teal-700">{prov.count}</span> Collings sale{prov.count === 1 ? "" : "s"}{prov.suburb ? ` in ${prov.suburb}` : ""}{prov.agent ? ` by ${prov.agent}` : ""} · last {prov.months} months
            </div>
            <div className="text-[11px] text-gray-400 mt-0.5 mb-3">source: collings_sales · verified first-party · traceable to records</div>
            <div className="flex flex-wrap gap-1.5">
              {(prov.records || []).slice(0, 12).map((r) => (
                <span key={r.sale_id} className="inline-flex items-center gap-1.5 text-[11px] rounded-md border border-gray-200 bg-white px-2 py-1 text-gray-500">
                  <code className="text-teal-700">{String(r.sale_id).slice(0, 8)}</code>
                  <span>· #{r.property_pk} · {r.sold_date ? String(r.sold_date).slice(0, 10) : "—"}{r.sold_price ? ` · $${Number(r.sold_price).toLocaleString()}` : ""}</span>
                </span>
              ))}
              {(prov.records || []).length > 12 && <span className="text-[11px] text-gray-400 self-center">+ {prov.records.length - 12} more</span>}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AiSeoNativeView;
